/**
 * "Your calendars" — the returning-visitor part of the landing page.
 *
 * Two lists, and the difference between them matters: calendars an account
 * owns, and calendars this browser holds an owner link for. A calendar already
 * attached to an account is not "held by a link" any more, so it says so.
 *
 * The second list is drawn from what this device remembers about each key
 * (`ownerTokens.note`) before anything is fetched, so someone holding ten links
 * is not made to wait for ten bundles. The keys are then checked a few at a
 * time in the background: a dead one is dropped, a live one refreshes its note.
 */
import type { Calendar, User } from '@circle/shared';
import { ApiError, api } from '../../app/api';
import { ownerTokens } from '../../app/owner-tokens';
import { el } from '../dom';

/** `name` and `claimed` are null when nothing is known about the key yet. */
export interface DeviceEntry {
  calendarId: string;
  name: string | null;
  claimed: boolean | null;
}

export interface CalendarsState {
  user: User | null;
  mine: Calendar[];
  onThisDevice: DeviceEntry[];
}

export const noCalendars: CalendarsState = { user: null, mine: [], onThisDevice: [] };

/** How many bundles are fetched at once while the keys are checked. */
export const VERIFY_CONCURRENCY = 3;

export function hasCalendars(state: CalendarsState): boolean {
  return state.mine.length > 0 || state.onThisDevice.length > 0;
}

/**
 * The state as this device knows it, at once; then, when `onUpdate` is given,
 * the same state again each time a key has been checked against the server.
 */
export async function loadCalendars(
  onUpdate?: (state: CalendarsState) => void,
): Promise<CalendarsState> {
  const me = await api.me().catch(() => null);
  const user = me?.user ?? null;
  const mine = me?.calendars ?? [];

  const owned = new Set(mine.map((c) => c.id));
  const stored = ownerTokens.list().filter((entry) => !owned.has(entry.calendarId));
  let onThisDevice: DeviceEntry[] = stored.map(({ calendarId }) => {
    const note = ownerTokens.note(calendarId);
    return { calendarId, name: note?.name ?? null, claimed: note?.claimed ?? null };
  });
  const state = (): CalendarsState => ({ user, mine, onThisDevice });

  if (onUpdate && stored.length) {
    void verifyKeys(
      stored.map((s) => s.calendarId),
      (calendarId, entry) => {
        onThisDevice = entry
          ? onThisDevice.map((e) => (e.calendarId === calendarId ? entry : e))
          : onThisDevice.filter((e) => e.calendarId !== calendarId);
        onUpdate(state());
      },
    );
  }
  return state();
}

/**
 * Check each key against the server, `VERIFY_CONCURRENCY` at a time. A key the
 * server refuses or that opens nothing is forgotten; a network failure is not
 * that, and the entry is left as it was. Each answer is reported as it lands.
 */
async function verifyKeys(
  ids: string[],
  report: (calendarId: string, entry: DeviceEntry | null) => void,
): Promise<void> {
  const queue = [...ids];
  const worker = async (): Promise<void> => {
    for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
      try {
        const { calendar } = await api.getCalendar(id);
        ownerTokens.setNote(id, { name: calendar.name, claimed: calendar.claimed });
        report(id, { calendarId: id, name: calendar.name, claimed: calendar.claimed });
      } catch (err) {
        if (err instanceof ApiError && [401, 403, 404].includes(err.status)) {
          ownerTokens.remove(id);
          report(id, null);
        }
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(VERIFY_CONCURRENCY, ids.length) }, worker));
}

/** The sections to show, or none at all when this visitor has no calendars. */
export function calendarSections(state: CalendarsState): HTMLElement[] {
  const out: HTMLElement[] = [];
  if (state.mine.length) {
    out.push(section('Your calendars', calendarList(state.mine)));
  }
  if (state.onThisDevice.length) {
    out.push(
      section(
        state.user ? 'Held by a link on this device' : 'On this device',
        deviceList(state.onThisDevice),
        deviceNote(state.onThisDevice),
      ),
    );
  }
  return out;
}

function section(title: string, list: HTMLElement, note?: string): HTMLElement {
  return el(
    'section',
    { class: 'lp-section lp-section--list' },
    el('h2', { text: title }),
    note ? el('p', { class: 'small muted', text: note }) : null,
    list,
  );
}

function calendarList(calendars: Calendar[]): HTMLElement {
  return el(
    'ul',
    { class: 'cal-list' },
    ...calendars.map((calendar) => row(calendar.id, calendar.name, visibilityLabel(calendar))),
  );
}

function deviceList(entries: DeviceEntry[]): HTMLElement {
  return el(
    'ul',
    { class: 'cal-list' },
    ...entries.map((entry) =>
      row(
        entry.calendarId,
        entry.name ?? 'A calendar on this device',
        entry.claimed === null
          ? 'open it to see'
          : entry.claimed
            ? 'attached to an account — sign in to manage keys'
            : 'held by a link',
      ),
    ),
  );
}

function deviceNote(entries: DeviceEntry[]): string {
  const known = entries.filter((c) => c.claimed !== null);
  if (known.length && known.every((c) => c.claimed)) {
    return 'These already belong to an account. Sign in to manage their keys.';
  }
  return 'The unattached ones are open to anyone with their owner link. Open one and choose Keys to attach it to your account.';
}

function row(id: string, name: string, meta: string): HTMLElement {
  return el(
    'li',
    {},
    el(
      'a',
      { href: `/circles/${id}` },
      el('span', { class: 'list__name', text: name }),
      el('span', { class: 'list__meta', text: meta }),
    ),
  );
}

function visibilityLabel(calendar: Calendar): string {
  return calendar.visibility === 'private' ? 'private' : 'shared by link';
}
